import TrackedGovLink from "./TrackedGovLink";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const COPY = {
  tools: {
    title: "Using Claude or ChatGPT at work? Set rules first",
    body: "Before staff paste company data into an AI tool, agree which tool is approved and what data may go in. Our free AI governance guide covers the policy, the data × AI tool matrix and a 90-day plan.",
    cta: "Read the AI governance guide",
  },
  rollout: {
    title: "Rolling out AI across the company? Govern it from day one",
    body: "Know every AI system you run, who owns it and what data it touches, before an auditor or regulator asks. Free AI governance templates and a 90-day plan for Malaysia and Singapore, with an AI risk management platform on the way.",
    cta: "See the AI governance guide",
  },
} as const;

/**
 * "Using Claude or ChatGPT at work? Set rules first" — internal link from the
 * Claude/ChatGPT pricing and privacy pages to the /ai-governance/ hub.
 */
export default function GovernanceCallout({
  locale = defaultLocale,
  className,
  variant = "tools",
  placement,
  href: hrefOverride,
  cta,
}: {
  locale?: Locale;
  className?: string;
  /** "tools" for Claude/ChatGPT pricing pages, "rollout" for service and agent pages. */
  variant?: keyof typeof COPY;
  /** Where the callout sits; sent with the GA4 `agk_internal_link` click event. */
  placement?: string;
  /**
   * Link target. "#section" points at a section of the localized hub; any other path is used
   * as given (the /ai-governance/ subpages are English-only). Defaults to the hub top.
   */
  href?: string;
  /** English link text, translated with t(); use it to vary the anchor text per page. */
  cta?: string;
}) {
  const copy = COPY[variant];
  const hub = getLocalizedPathname("/ai-governance/", locale);
  const href = !hrefOverride ? hub : hrefOverride.startsWith("#") ? `${hub}${hrefOverride}` : hrefOverride;
  return (
    <aside
      aria-label={t(locale, "AI governance")}
      className={cn(
        "not-prose mx-auto flex max-w-4xl flex-col gap-4 rounded-2xl border border-info/25 bg-info/5 p-6 sm:flex-row sm:items-center sm:p-7",
        className,
      )}
    >
      <ShieldCheck className="h-8 w-8 shrink-0 text-info" aria-hidden />
      <div className="flex-1">
        <p className="font-display text-lg font-semibold text-neutral-emphasis">
          {t(locale, copy.title)}
        </p>
        <p className="mt-1 text-sm leading-relaxed text-neutral-muted">
          {t(locale, copy.body)}
        </p>
      </div>
      <TrackedGovLink
        href={href}
        location={placement ?? `callout_${variant}`}
        className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-primary underline underline-offset-4 hover:text-primary-emphasis"
      >
        {t(locale, cta ?? copy.cta)}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </TrackedGovLink>
    </aside>
  );
}
